// imagePrintPreview.jsx - Reusable image print preview component
import React from 'react';
import { imperativeAlert } from '../contexts/ToastContext';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

export const openImagePrintPreview = async (images, options = {}) => {
  // Ensure we have valid image data with preview URLs
  const validImages = images.filter(img => img.previewUrl || img.preview);
  
  if (validImages.length === 0) {
    imperativeAlert('No images available for printing. Please ensure images are properly loaded.');
    return;
  }

  // On mobile, window.open + window.print() is blocked by the OS/WebView.
  // Use the Web Share API (which includes native Print) or fall back to download.
  const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1);
  if (isMobileDevice) {
    try {
      if (Capacitor.isNativePlatform()) {
        // Use @capacitor/share for reliable native Android & iOS sharing (includes Print)
        // Share one at a time — Capacitor Share accepts a single URL
        for (const img of validImages) {
          await Share.share({
            title: options.title || 'Print Photo',
            url: img.previewUrl || img.preview,
            dialogTitle: 'Print or share your photo',
          });
        }
      } else {
        // PWA / mobile browser — use Web Share API
        const filesToShare = await Promise.all(
          validImages.map(async (img, i) => {
            const res = await fetch(img.previewUrl || img.preview);
            const blob = await res.blob();
            const ext = blob.type.includes('png') ? 'png' : 'jpg';
            return new File([blob], `${img.name || `photo-${i + 1}`}.${ext}`, { type: blob.type });
          })
        );
        if (navigator.share && navigator.canShare && navigator.canShare({ files: filesToShare })) {
          await navigator.share({ files: filesToShare, title: options.title || 'Print Photos' });
        } else {
          filesToShare.forEach(file => {
            const blobUrl = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = file.name;
            a.click();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
          });
          imperativeAlert('Photo(s) saved — open them in your gallery or files app to print.');
        }
      }
    } catch (err) {
      console.error('Mobile print/share error:', err);
      imperativeAlert('Unable to share image. Please try saving the photo first, then print from your gallery.');
    }
    return;
  }

  const printPreviewWindow = window.open('', '_blank', 'width=800,height=600,resizable=yes');
  
  // Default options
  const {
    showFileName = true,
    showSizeInfo = true,
    defaultWidth = 15,
    defaultHeight = 10,
    title = 'Image Print Preview'
  } = options;

  // Create image elements with proper src attributes
  const imageElements = validImages.map((image, index) => `
    <div class="image-container" id="imageContainer-${index}">
      <div class="size-info" id="sizeInfo-${index}">${defaultWidth}.0 cm × ${defaultHeight}.0 cm</div>
      ${showFileName ? `<h3 class="file-name">${image.name || 'Image'}</h3>` : ''}
      <img 
        src="${image.previewUrl || image.preview}" 
        class="resizable-image" 
        id="printImage-${index}"
        style="width: ${defaultWidth}cm; height: ${defaultHeight}cm;"
        draggable="false"
        onload="updateSizeInfo(${index})"
        alt="${image.name || 'Image'}"
        data-original-width="${defaultWidth}cm"
        data-original-height="${defaultHeight}cm"
      >
      ${showSizeInfo ? `<p class="file-info">Original: ${image.type || 'N/A'} | File size: ${image.size || 'N/A'}</p>` : ''}
    </div>
  `).join('');

  const htmlContent = generatePrintPreviewHTML(title, imageElements, validImages, {
    showFileName,
    showSizeInfo,
    defaultWidth,
    defaultHeight
  });

  printPreviewWindow.document.write(htmlContent);
  printPreviewWindow.document.close();
};

const generatePrintPreviewHTML = (title, imageElements, validImages, options) => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        ${getPrintPreviewStyles()}
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="print-options">
          <label>
            <input type="checkbox" id="showFileName" ${options.showFileName ? 'checked' : ''}> Show file name
          </label>
          <label style="margin-left: 15px;">
            <input type="checkbox" id="showSizeInfo" ${options.showSizeInfo ? 'checked' : ''}> Show size information
          </label>
        </div>
        
        <div class="controls">
          <div class="control-group">
            <label>Width (cm):</label>
            <input type="number" id="widthInput" value="${options.defaultWidth}" min="1" max="30" step="0.1">
          </div>
          <div class="control-group">
            <label>Height (cm):</label>
            <input type="number" id="heightInput" value="${options.defaultHeight}" min="1" max="30" step="0.1">
          </div>
          <button onclick="applySize()">Apply Size</button>
          <button onclick="resetSize()">Reset Size</button>
          <button onclick="printImage()" style="background: #666;">Print Image</button>
        </div>
        
        <!-- Images Container -->
        <div id="imagesContainer">
          ${imageElements}
        </div>
      </div>

      <script>
        ${getPrintPreviewScript(validImages, options)}
      </script>
    </body>
    </html>
  `;
};

const getPrintPreviewStyles = () => {
  return `
    body { 
      font-family: Arial, sans-serif; 
      margin: 20px; 
      background: #f5f5f5;
    }
    .print-container {
      background: white;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      max-width: 800px;
      margin: 0 auto;
    }
    .image-container {
      text-align: center;
      margin: 20px 0;
      position: relative;
      page-break-inside: avoid;
    }
    .resizable-image {
      max-width: 100%;
      max-height: 500px;
      border: 2px dashed #ccc;
      cursor: move;
      user-select: none;
      display: block;
      margin: 0 auto;
    }
    .size-info {
      background: #333;
      color: white;
      padding: 5px 10px;
      border-radius: 4px;
      position: absolute;
      top: 10px;
      left: 10px;
      font-size: 12px;
    }
    .controls {
      display: flex;
      gap: 10px;
      margin: 20px 0;
      flex-wrap: wrap;
      align-items: center;
    }
    .control-group {
      display: flex;
      gap: 5px;
      align-items: center;
    }
    .print-options {
      margin: 20px 0;
      padding: 15px;
      background: #f8f9fa;
      border-radius: 4px;
    }
    input[type="number"] {
      width: 80px;
      padding: 5px;
      border: 1px solid #ddd;
      border-radius: 4px;
    }
    button {
      padding: 8px 16px;
      background: #666;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }
    button:hover {
      background: #fa4848;
    }
    
    /* PRINT STYLES - Hide controls but preserve image sizes */
    @media print {
      body { 
        background: white; 
        margin: 0;
        padding: 0;
      }
      .print-container { 
        box-shadow: none; 
        padding: 0;
        max-width: none;
      }
      .controls, 
      .print-options, 
      .size-info,
      .file-name,
      .file-info {
        display: none !important;
      }
      .resizable-image { 
        border: none;
        max-width: 100% !important;
        max-height: none !important;
        min-width: 5cm !important;
        min-height: 3cm !important;
      }
      .image-container { 
        margin: 10mm 0;
        page-break-inside: avoid;
        page-break-after: auto;
      }
    }
  `;
};

const getPrintPreviewScript = (validImages, options) => {
  return `
    // Store images data
    const images = ${JSON.stringify(validImages)};
    
    // Store current dimensions for each image
    let imageDimensions = {};
    const dpi = 96; // Standard screen DPI for cm conversion

    // Initialize images on page load
    document.addEventListener('DOMContentLoaded', function() {
      setupEventListeners();
      images.forEach((_, index) => {
        updateSizeInfo(index);
        // Store initial dimensions
        const img = document.getElementById('printImage-' + index);
        if (img) {
          imageDimensions[index] = {
            width: '${options.defaultWidth}cm',
            height: '${options.defaultHeight}cm'
          };
        }
      });
    });

    function setupEventListeners() {
      // Show/hide file name
      const showFileNameCheckbox = document.getElementById('showFileName');
      if (showFileNameCheckbox) {
        showFileNameCheckbox.addEventListener('change', function() {
          document.querySelectorAll('.file-name').forEach(element => {
            element.style.display = this.checked ? 'block' : 'none';
          });
        });
      }

      // Show/hide size info
      const showSizeInfoCheckbox = document.getElementById('showSizeInfo');
      if (showSizeInfoCheckbox) {
        showSizeInfoCheckbox.addEventListener('change', function() {
          document.querySelectorAll('.file-info').forEach(element => {
            element.style.display = this.checked ? 'block' : 'none';
          });
        });
      }
    }

    let isResizing = false;
    let currentImage = null;
    let startX, startY, startWidth, startHeight;

    function cmToPx(cm) {
      return (cm * dpi) / 2.54;
    }

    function pxToCm(px) {
      return (px * 2.54) / dpi;
    }

    function updateSizeInfo(index) {
      const img = document.getElementById('printImage-' + index);
      const sizeInfo = document.getElementById('sizeInfo-' + index);
      if (img && sizeInfo) {
        const widthCm = pxToCm(img.offsetWidth).toFixed(1);
        const heightCm = pxToCm(img.offsetHeight).toFixed(1);
        sizeInfo.textContent = widthCm + ' cm × ' + heightCm + ' cm';
        
        // Store current dimensions for printing
        imageDimensions[index] = {
          width: widthCm + 'cm',
          height: heightCm + 'cm'
        };
      }
    }

    function applySize() {
      const widthCm = parseFloat(document.getElementById('widthInput').value) || ${options.defaultWidth};
      const heightCm = parseFloat(document.getElementById('heightInput').value) || ${options.defaultHeight};
      
      images.forEach((_, index) => {
        const img = document.getElementById('printImage-' + index);
        if (img) {
          img.style.width = widthCm + 'cm';
          img.style.height = heightCm + 'cm';
          img.style.maxWidth = 'none';
          img.style.maxHeight = 'none';
          updateSizeInfo(index);
        }
      });
    }

    function resetSize() {
      images.forEach((_, index) => {
        const img = document.getElementById('printImage-' + index);
        if (img) {
          const originalWidth = img.getAttribute('data-original-width') || '${options.defaultWidth}cm';
          const originalHeight = img.getAttribute('data-original-height') || '${options.defaultHeight}cm';
          
          img.style.width = originalWidth;
          img.style.height = originalHeight;
          img.style.maxWidth = '100%';
          img.style.maxHeight = '500px';
          updateSizeInfo(index);
          
          document.getElementById('widthInput').value = '${options.defaultWidth}';
          document.getElementById('heightInput').value = '${options.defaultHeight}';
        }
      });
    }

    function printImage() {
      // Before printing, ensure all images have their current dimensions preserved
      images.forEach((_, index) => {
        const img = document.getElementById('printImage-' + index);
        if (img && imageDimensions[index]) {
          img.style.width = imageDimensions[index].width;
          img.style.height = imageDimensions[index].height;
        }
      });
      
      // Small delay to ensure styles are applied before printing
      setTimeout(() => {
        window.print();
      }, 100);
    }

    // Mouse resize functionality
    document.addEventListener('mousedown', function(e) {
      if (e.target.classList.contains('resizable-image')) {
        isResizing = true;
        currentImage = e.target;
        startX = e.clientX;
        startY = e.clientY;
        startWidth = parseInt(document.defaultView.getComputedStyle(currentImage).width, 10);
        startHeight = parseInt(document.defaultView.getComputedStyle(currentImage).height, 10);
        e.preventDefault();
      }
    });

    document.addEventListener('mousemove', function(e) {
      if (isResizing && currentImage) {
        const width = startWidth + (e.clientX - startX);
        const height = startHeight + (e.clientY - startY);
        
        currentImage.style.width = width + 'px';
        currentImage.style.height = height + 'px';
        currentImage.style.maxWidth = 'none';
        currentImage.style.maxHeight = 'none';
        
        // Update size inputs and info
        const widthCm = pxToCm(width).toFixed(1);
        const heightCm = pxToCm(height).toFixed(1);
        
        document.getElementById('widthInput').value = widthCm;
        document.getElementById('heightInput').value = heightCm;
        
        // Update all size info displays and store dimensions
        images.forEach((_, index) => {
          updateSizeInfo(index);
        });
      }
    });

    document.addEventListener('mouseup', function() {
      isResizing = false;
      currentImage = null;
    });
  `;
};

// Export additional utility functions if needed
export const printUtils = {
  validateImages: (images) => {
    return images.filter(img => img.previewUrl || img.preview);
  },
  getDefaultOptions: () => ({
    showFileName: true,
    showSizeInfo: true,
    defaultWidth: 15,
    defaultHeight: 10,
    title: 'Image Print Preview'
  })
};

export default openImagePrintPreview;